import {timelineItemKey} from '@github-ui/timeline-items/TimelineItemKey'

import {rollupEvents} from '../../utils/timeline-rollups'
import type {TimelineItem} from './IssueTimeline'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'

/**
 * Assembly of the rendered timeline list: no Relay imports, no React, so the same ordering, dedupe
 * and marker rules can back a second data source.
 */

export type TimelineCounters = {
  frontItems: number
  highlightItems: number
  highlightStartPosition: number
  backItems: number
}

/**
 * A "load more" marker as decided by the assembly: where it goes and what it says. Callbacks are
 * attached by the caller, so this stays free of the ref-reading loaders React Compiler forbids
 * touching during render.
 */
export type TimelineLoadMarker = {
  type: 'load-marker'
  position: 'top' | 'bottom'
  numberOfRemainingItems: number
  isCurrentlyLoading: boolean
}

export type TimelineSlot = Exclude<TimelineItem, {type: 'load'}> | TimelineLoadMarker

/** Dedupes timeline items by their stable key, keeping the first occurrence. */
export const dedupeItems = (items: IssueTimelineItemFragment[]) =>
  items.reduce(
    ({keys, values}, item) => {
      const key = timelineItemKey(item)
      if (!keys.has(key)) {
        keys.add(key)
        values.push(item)
      }

      return {keys, values}
    },
    {keys: new Set<string>(), values: [] as IssueTimelineItemFragment[]},
  ).values

/** Subtracts the second list from the first, comparing by stable key. */
export const subtractItems = (items: IssueTimelineItemFragment[], subtract: IssueTimelineItemFragment[]) =>
  items.filter(item => !subtract.some(subtractItem => timelineItemKey(subtractItem) === timelineItemKey(item)))

export function isHighlightReady(
  totalItemCount: number,
  backItems: number,
  timelineCounters: TimelineCounters,
  highlightIsPreloaded: boolean,
) {
  if (timelineCounters.highlightItems === 0 && !highlightIsPreloaded) return false

  const highlightedItemsInBack =
    timelineCounters.highlightStartPosition + timelineCounters.highlightItems > totalItemCount - 15
  const backItemsPending = totalItemCount > 15 && backItems === 0

  // Don't scroll yet: the highlight overlaps the back items and those haven't arrived.
  if (highlightedItemsInBack && backItemsPending) return false

  // Nor while rows above the highlight are missing: inserting them afterwards pushes the target
  // down by their height, and nothing scrolls again.
  if (timelineCounters.highlightStartPosition > 0 && timelineCounters.frontItems === 0) return false

  return true
}

export function countItemsRemaining({
  hasLazyLoadedHighlights,
  timelineCounters,
  totalItemCount,
  frontItemCount,
  backItemCount,
}: {
  hasLazyLoadedHighlights: boolean
  timelineCounters: TimelineCounters
  totalItemCount: number
  frontItemCount: number
  backItemCount: number
}): {front: number; back: number} {
  const unfilteredRemaining = totalItemCount - frontItemCount - backItemCount
  // Front-page overlap was removed during dedupe, so the unique window cannot begin before it.
  const uniqueHighlightStartPosition = Math.max(timelineCounters.highlightStartPosition, timelineCounters.frontItems)

  return {
    front: Math.max(
      hasLazyLoadedHighlights ? uniqueHighlightStartPosition - timelineCounters.frontItems : unfilteredRemaining,
      0,
    ),
    back: Math.max(
      hasLazyLoadedHighlights
        ? totalItemCount - (uniqueHighlightStartPosition + timelineCounters.highlightItems) - timelineCounters.backItems
        : unfilteredRemaining,
      0,
    ),
  }
}

export type AssembleTimelineItemsParams = {
  frontItems: IssueTimelineItemFragment[]
  backItems: IssueTimelineItemFragment[]
  emptyBackItems: IssueTimelineItemFragment[]
  uniqueLoadedHighlightItems: IssueTimelineItemFragment[]
  totalItemCount: number
  itemsRemainingFront: number
  itemsRemainingBack: number
  hasLazyLoadedHighlights: boolean
  loadingInitialBackItems: boolean
  page?: number
}

/**
 * Combines the front, highlight, and back sections into the rendered list, injecting "load more"
 * markers between them. Returns null for the error state: items are expected but none arrived.
 */
export function assembleTimelineItems({
  frontItems,
  backItems,
  emptyBackItems,
  uniqueLoadedHighlightItems,
  totalItemCount,
  itemsRemainingFront,
  itemsRemainingBack,
  hasLazyLoadedHighlights,
  loadingInitialBackItems,
  page,
}: AssembleTimelineItemsParams): TimelineSlot[] | null {
  // Return null (error state) when we expect items but none have loaded in
  // any connection. We must also check back items because mutations (e.g.
  // adding a comment) append edges to the back connection and increment
  // totalCount on the front connection. Without this check an initially-empty
  // timeline would return null as soon as the first comment is added.
  if (frontItems.length === 0 && emptyBackItems.length === 0 && backItems.length === 0 && totalItemCount !== 0) {
    return null
  }

  // If we loaded duplicated records from the front and back load, we dedupe them to prevent accidental duplicates
  //
  // This can happen if for some reason we get more items than there is in the issue
  // like when an event is deleted or a mismatch from live updates
  if (
    frontItems.length + uniqueLoadedHighlightItems.length + backItems.length + emptyBackItems.length >=
    totalItemCount
  ) {
    return rollupEvents(
      dedupeItems([...frontItems, ...uniqueLoadedHighlightItems, ...backItems, ...emptyBackItems]),
    ).map(item => ({
      type: 'event',
      ...item,
    }))
  }
  // Otherwise we render both arrays and inject the load buttons in the middle

  const frontTimelineItems: TimelineSlot[] = rollupEvents(frontItems).map(item => ({
    type: 'event',
    ...item,
  }))

  const loadMoreFront: TimelineSlot[] =
    itemsRemainingFront === 0 || page
      ? []
      : [
          {
            type: 'load-marker',
            position: 'top',
            numberOfRemainingItems: itemsRemainingFront,
            isCurrentlyLoading: loadingInitialBackItems && uniqueLoadedHighlightItems.length === 0,
          },
        ]

  const highlightTimelineItems: TimelineSlot[] = !hasLazyLoadedHighlights
    ? []
    : rollupEvents(uniqueLoadedHighlightItems).map(item => ({
        type: 'event',
        ...item,
      }))

  const loadMoreBack: TimelineSlot[] =
    !hasLazyLoadedHighlights || itemsRemainingBack === 0 || page
      ? []
      : [
          {
            type: 'load-marker',
            position: 'bottom',
            numberOfRemainingItems: itemsRemainingBack,
            isCurrentlyLoading: loadingInitialBackItems && uniqueLoadedHighlightItems.length > 0,
          },
        ]

  const backTimelineItems: TimelineSlot[] = rollupEvents(dedupeItems([...backItems, ...emptyBackItems])).map(item => ({
    type: 'event',
    ...item,
  }))

  return [...frontTimelineItems, ...loadMoreFront, ...highlightTimelineItems, ...loadMoreBack, ...backTimelineItems]
}
